import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { Container } from "reactstrap";
import ActionMenu from "../../../components/Common/ActionMenu";
import { get, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * AdminDeletedUsersPage — view and manage deleted users/members.
 * Provides listing of deleted users with Restore Member and Delete Permanently options.
 * ---------------------------------------------------------------------- */

const AdminDeletedUsersPage = () => {
  document.title = "Deleted Members | Omaha Food Lovers";
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("deleted_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showDeletePermanentlyModal, setShowDeletePermanentlyModal] = useState(false);
  const [userToReactivate, setUserToReactivate] = useState(null);
  const [userToDeletePermanently, setUserToDeletePermanently] = useState(null);
  const [reactivating, setReactivating] = useState(false);
  const [deletingPermanently, setDeletingPermanently] = useState(false);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const columns = [
    { key: "name", label: "Name", width: "1.5fr" },
    { key: "email", label: "Email", width: "1.5fr" },
    { key: "status", label: "Status", width: "0.8fr", render: () => "Deleted" },
    { key: "pointsBalance", label: "Points Balance", width: "1fr" },
    { key: "linkedCardsCount", label: "Linked Cards", width: "1fr" },
    { key: "vouchersCount", label: "Vouchers", width: "1fr" },
    { key: "referralsCount", label: "Referrals", width: "1fr" },
    { key: "gamesCompleted", label: "Games Completed", width: "1fr" },
    {
      key: "actions",
      label: "",
      width: "60px",
      align: "right",
      className: "rp-table__cell--actions",
      render: (row) => (
        <ActionMenu
          isOpen={activeDropdown === row.id}
          onToggle={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
          items={[
            {
              label: "Restore Member",
              icon: "tick",
              onClick: () => handleReactivate(row.id),
            },
            {
              label: "Delete Permanently",
              icon: "delete",
              onClick: () => handleDeletePermanently(row.id),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);
    params.append("status", "deleted");

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const transformUserData = (usersArray) =>
    usersArray.map((userItem) => ({
      id: userItem.id,
      firstName: userItem.first_name || "",
      lastName: userItem.last_name || "",
      name: userItem.full_name || `${userItem.first_name || ""} ${userItem.last_name || ""}`.trim() || "Unknown",
      email: userItem.email || "N/A",
      mobile: userItem.mobile || "N/A",
      isAdmin: userItem.is_admin === "1" || userItem.is_admin === 1,
      isFreeMember: userItem.is_free_member === 1,
      isActive: userItem.is_active === "1" || userItem.is_active === 1,
      pointsBalance: userItem.points_balance || "0",
      linkedCardsCount: userItem.linked_cards_count || 0,
      vouchersCount: userItem.vouchers_count || 0,
      referralsCount: userItem.referrals_count || 0,
      gamesCompleted: userItem.games_completed || 0,
    }));

  const fetchUsers = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminAllDeactivatedUsers}&${buildQueryParams(pageNum)}`);

      if (response.data?.status || response.data?.success) {
        const data = response.data.data;
        const newUsers = data.data || [];
        const transformedUsers = transformUserData(newUsers);

        if (isLoadMore) {
          setUsers((prev) => [...prev, ...transformedUsers]);
        } else {
          setUsers(transformedUsers);
        }

        const currentPage = data.current_page || 1;
        const lastPage = data.last_page || 1;
        setHasMore(currentPage < lastPage);
        setPage(currentPage);
      }
    } catch (error) {
      console.error("Failed to fetch deleted users:", error);
      toastr.error("Failed to fetch deleted users");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchUsers(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleReactivate = (userId) => {
    setUserToReactivate(userId);
    setShowReactivateModal(true);
    setActiveDropdown(null);
  };

  const confirmReactivate = async () => {
    if (!userToReactivate) return;

    setReactivating(true);
    try {
      const response = await get(endpoints.adminRestoreUser.replace("{{user_id}}", userToReactivate));
      if (response.data?.success || response.data?.status) {
        toastr.success("User restored successfully");
        setShowReactivateModal(false);
        setUserToReactivate(null);
        fetchUsers(1, false);
      }
    } catch (error) {
      console.error("Failed to restore user:", error);
      toastr.error("Failed to restore user");
    } finally {
      setReactivating(false);
    }
  };

  const cancelReactivate = () => {
    setShowReactivateModal(false);
    setUserToReactivate(null);
  };

  const handleDeletePermanently = (userId) => {
    setUserToDeletePermanently(userId);
    setShowDeletePermanentlyModal(true);
    setActiveDropdown(null);
  };

  const confirmDeletePermanently = async () => {
    if (!userToDeletePermanently) return;

    setDeletingPermanently(true);
    try {
      const response = await del(endpoints.adminDeleteUserPermanently.replace("{{user_id}}", userToDeletePermanently));
      if (response.data?.success || response.data?.status) {
        toastr.success("User permanently deleted");
        setShowDeletePermanentlyModal(false);
        setUserToDeletePermanently(null);
        fetchUsers(1, false);
      }
    } catch (error) {
      console.error("Failed to delete user permanently:", error);
      toastr.error("Failed to delete user permanently");
    } finally {
      setDeletingPermanently(false);
    }
  };

  const cancelDeletePermanently = () => {
    setShowDeletePermanentlyModal(false);
    setUserToDeletePermanently(null);
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "deleted_at",
      options: [
        { value: "deleted_at", label: "Deleted At" },
        { value: "name", label: "Name" },
        { value: "email", label: "Email" },
        { value: "points_balance", label: "Points Balance" },
        { value: "vouchers_count", label: "Vouchers Count" },
        { value: "referrals_count", label: "Referrals Count" },
        { value: "games_completed", label: "Games Completed" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchUsers(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 className="rp-card__title" style={{ margin: 0 }}>Deleted Members</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of deleted member accounts and recovery options.</p>
            </div>
            <SecondaryButton
              variant="light-grey"
              onClick={() => navigate("/admin/users")}
              icon={cancelIcon}
              iconPosition="right"
            >
              Back to Active Members
            </SecondaryButton>
           
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by name or email"
            filters={filters}
          />

          <DataTable
            columns={columns}
            rows={users}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </div>
      </Container>

      {/* Restore Member Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showReactivateModal}
        title="Restore Member"
        message="Are you sure you want to restore this member? They will regain access to their account."
        onCancel={cancelReactivate}
        onConfirm={confirmReactivate}
        itemName="member"
        confirmButtonText="Restore Member"
        confirmButtonDisabled={reactivating}
      />

      {/* Delete Permanently Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeletePermanentlyModal}
        title="Delete Permanently"
        message="Are you sure you want to permanently delete this member? This action cannot be undone and all data will be lost."
        onCancel={cancelDeletePermanently}
        onConfirm={confirmDeletePermanently}
        itemName="member"
        confirmButtonText="Delete Permanently"
        confirmButtonDisabled={deletingPermanently}
      />
    </div>
  );
};

AdminDeletedUsersPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminDeletedUsersPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminDeletedUsersPage;
